/**
 * Edge classification utilities for multi-unit templates
 *
 * Key principle: "Every unit has all its walls. Only one unit RENDERS each shared wall."
 * - Each unit maintains complete boundary for collision detection
 * - Rendering uses classification to avoid duplicate walls
 */

import type { UnitEdge, EdgeSide, DoorSpec } from '../types'

/**
 * Door configuration for createBoxUnitEdges
 *
 * Supports:
 * - front: Always available (main entrance, faces ramp/taxiway)
 * - back: Always available (rear doors)
 * - left: All units (exterior door for last unit, partition door for interior units)
 * - right: All units (exterior door for first unit, partition door for interior units)
 *
 * NOTE: left/right are swapped relative to coordinate system to match user perspective
 * when viewing the building from outside (facing the front).
 *
 * Interior units can have partition doors (pass-through between adjacent units).
 */
export interface BoxUnitDoorConfig {
  /** Front wall doors (always available - faces ramp/taxiway) */
  front?: DoorSpec[]
  /** Back wall doors (always available - rear of unit) */
  back?: DoorSpec[]
  /** Left wall doors (exterior for last unit, partition for interior units) */
  left?: DoorSpec[]
  /** Right wall doors (exterior for first unit, partition for interior units) */
  right?: DoorSpec[]
}

/**
 * Create edges for a box unit in a single row
 *
 * @param unitIndex - Index of this unit (0-based)
 * @param unitCount - Total number of units in row
 * @param unitCode - Code prefix for unit IDs
 * @param doorConfig - Per-wall door configuration
 * @param skipPartitions - If true, mark partition walls as 'internal' (open bay mode)
 * @returns Array of 4 edges (front, back, left, right)
 */
export function createBoxUnitEdges(
  unitIndex: number,
  unitCount: number,
  unitCode: string,
  doorConfig?: BoxUnitDoorConfig | DoorSpec[], // Support legacy array for front wall only
  skipPartitions: boolean = false
): UnitEdge[] {
  const isFirstUnit = unitIndex === 0
  const isLastUnit = unitIndex === unitCount - 1

  // Normalize door config - support legacy array format (front wall only)
  let doors: BoxUnitDoorConfig = {}
  if (Array.isArray(doorConfig)) {
    // Legacy format: DoorSpec[] means front wall doors
    doors = { front: doorConfig }
  } else if (doorConfig) {
    doors = doorConfig
  }

  // Note: Interior units can now have left/right doors (partition doors)
  // These create pass-through openings between adjacent units

  // Helper to generate neighbor unit code
  const neighborCode = (index: number) => `${unitCode}-${String(index + 1).padStart(2, '0')}`

  return [
    // Back edge - exterior (back wall, can have doors)
    {
      side: 'back',
      type: 'exterior',
      canBeShared: false,
      openings: doors.back,
    },
    // Front edge - exterior with doors (main entrance, faces ramp/taxiway)
    {
      side: 'front',
      type: 'exterior',
      canBeShared: false,
      openings: doors.front,
    },
    // Left edge - partition, internal (open bay), or exterior
    // Last unit: exterior wall; Interior units: partition (or internal if skipPartitions)
    // Doors allowed on all edges (partition doors create pass-through between units)
    {
      side: 'left',
      type: isLastUnit ? 'exterior' : (skipPartitions ? 'internal' : 'partition'),
      canBeShared: !isLastUnit,
      sharedWith: isLastUnit ? null : neighborCode(unitIndex + 1),
      openings: doors.left,  // Allow doors on partition walls
    },
    // Right edge - partition, internal (open bay), or exterior
    // First unit: exterior wall; Interior units: partition (or internal if skipPartitions)
    // Doors allowed on all edges (partition doors create pass-through between units)
    {
      side: 'right',
      type: isFirstUnit ? 'exterior' : (skipPartitions ? 'internal' : 'partition'),
      canBeShared: !isFirstUnit,
      sharedWith: isFirstUnit ? null : neighborCode(unitIndex - 1),
      openings: doors.right,  // Allow doors on partition walls
    },
  ]
}

/**
 * Determine if this unit should render a shared edge
 *
 * Convention: The unit with the LOWER index renders the shared wall
 * This prevents duplicate rendering
 *
 * After swap (to match visual appearance):
 * - Left edges are shared with higher-index neighbors → this unit renders
 * - Right edges are shared with lower-index neighbors → neighbor renders
 *
 * @param edge - Edge to check
 * @param unitIndex - Index of this unit
 * @returns true if this unit should render the edge
 */
export function shouldRenderEdge(edge: UnitEdge, _unitIndex: number): boolean {
  // Always render non-shared edges
  if (!edge.canBeShared || !edge.sharedWith) {
    return true
  }

  // For shared edges, check convention (swapped to match visual)
  // Left edges are shared with higher-index neighbors, so this unit renders it
  // Right edges are shared with lower-index neighbors, so neighbor renders it
  if (edge.side === 'left') {
    // This unit renders the wall (we have lower index than neighbor)
    return true
  }

  if (edge.side === 'right') {
    // Lower-index neighbor renders this wall
    return false
  }

  // For other shared edges (future: front/back in double-row), use position
  return true
}

/**
 * Get edges that should be rendered for a unit
 *
 * @param edges - All unit edges
 * @param unitIndex - Index of this unit
 * @returns Filtered edges to render
 */
export function getEdgesToRender(edges: UnitEdge[], unitIndex: number): UnitEdge[] {
  return edges.filter(edge => shouldRenderEdge(edge, unitIndex))
}

/**
 * Get edge by side
 *
 * @param edges - Array of edges
 * @param side - Side to find
 * @returns Edge or undefined
 */
export function getEdgeBySide(edges: UnitEdge[], side: EdgeSide): UnitEdge | undefined {
  return edges.find(e => e.side === side)
}

/**
 * Check if edge has door openings
 *
 * @param edge - Edge to check
 * @returns true if edge has at least one door
 */
export function edgeHasDoors(edge: UnitEdge): boolean {
  return (edge.openings?.length ?? 0) > 0
}

/**
 * Get total door width on an edge
 *
 * @param edge - Edge with doors
 * @returns Total width of all doors
 */
export function getTotalDoorWidth(edge: UnitEdge): number {
  return edge.openings?.reduce((sum, door) => sum + door.width_ft, 0) ?? 0
}

/**
 * Clone edges with new door configuration
 *
 * @param edges - Original edges
 * @param newDoors - New door specifications
 * @param doorSide - Which side has doors (default: 'front')
 * @returns Cloned edges with updated doors
 */
export function updateEdgeDoors(
  edges: UnitEdge[],
  newDoors: DoorSpec[],
  doorSide: EdgeSide = 'front'
): UnitEdge[] {
  return edges.map(edge => {
    if (edge.side === doorSide) {
      return { ...edge, openings: newDoors }
    }
    return edge
  })
}

/**
 * Calculate wall segments for rendering (excluding door openings)
 *
 * @param edgeLength - Total edge length
 * @param doors - Door specifications on this edge
 * @returns Array of wall segment positions [start, end]
 */
export function calculateWallSegments(
  edgeLength: number,
  doors?: DoorSpec[]
): Array<[number, number]> {
  if (!doors || doors.length === 0) {
    return [[0, edgeLength]]
  }

  const segments: Array<[number, number]> = []
  const sortedDoors = [...doors].sort((a, b) => a.offset_ft - b.offset_ft)

  let currentPos = 0
  for (const door of sortedDoors) {
    // Wall segment before door
    if (door.offset_ft > currentPos) {
      segments.push([currentPos, door.offset_ft])
    }
    // Move past door
    currentPos = door.offset_ft + door.width_ft
  }

  // Wall segment after last door
  if (currentPos < edgeLength) {
    segments.push([currentPos, edgeLength])
  }

  return segments
}
